import { get, keyBy } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { getIntegrationEmployees } from '../../../crud/integration.crud'
import { GWS, OFFICE365 } from '../Employees/utils'
import { ENGAGE } from './engage'
import { INTEGRATION_APPS } from './integrations'
import { SECURE } from './secure'

const secureModules = ['secure-essentials', 'secure-premium', 'nfr-secure-essentials']
const engageModules = ['engage-essentials', 'nfr-engage-essentials']
const allModules = secureModules + engageModules

export const useIntegrationEmployees = ({ integrationAppName, selectedCustomerId, product }) => {
  const integrationEmployeesRef = useRef([])
  const [selectedIntegration, setSelectedIntegration] = useState({})
  const [selectedIntegrationType, setSelectedIntegrationType] = useState(OFFICE365)
  const [hasIntegration, setHasIntegration] = useState(false)
  const [integrationMap, setIntegrationMap] = useState({})

  useEffect(() => {
    if (hasIntegration) {
      setSelectedIntegration(integrationMap)
      setSelectedIntegrationType(integrationAppName)
    } else {
      setSelectedIntegration({})
      setSelectedIntegrationType(integrationAppName ?? OFFICE365)
    }
  }, [hasIntegration, integrationAppName, integrationMap])

  let requiredModules
  if (product === SECURE) {
    requiredModules = secureModules
  } else if (product === ENGAGE) {
    requiredModules = engageModules
  } else {
    // Assume any of the following will allow the employees to be returned.
    requiredModules = allModules
  }

  // If DUO, assume microsoft
  if (integrationAppName === INTEGRATION_APPS.DUO_SECURITY) {
    // eslint-disable-next-line no-param-reassign
    integrationAppName = INTEGRATION_APPS.OFFICE_365
  }

  const requestIntegrationEmployees = async ({ force = false } = {}) => {
    if (!selectedCustomerId) return null

    try {
      if (force || integrationEmployeesRef.current.length === 0) {
        const response = await getIntegrationEmployees(selectedCustomerId, integrationAppName, requiredModules)
        const data = get(response, 'data', [])
        setHasIntegration(response?.status === 200)
        setIntegrationMap(keyBy(data, 'employeeId'))

        integrationEmployeesRef.current = data ?? []
      }
    } catch (err) {
      setHasIntegration(false)
      console.error(`getIntegrationEmployees ${integrationAppName} error`, err)
    }

    return integrationEmployeesRef.current
  }

  const updateSelectedIntegration = useCallback(
    (integrationType) => {
      setSelectedIntegration(integrationMap)
      if (integrationType === OFFICE365) {
        setSelectedIntegrationType(OFFICE365)
      } else {
        setSelectedIntegrationType(GWS)
      }
    },
    [integrationMap],
  )

  return {
    hasIntegration,
    integrationEmployeesRef,
    requestIntegrationEmployees,
    selectedIntegration,
    selectedIntegrationType,
    updateSelectedIntegration,
  }
}

const fetchIntegrationEmployees = async ({
  customerId,
  integrationType,
  setLoading,
  setIntegrationMap,
  setHasIntegration,
}) => {
  setLoading(true)
  try {
    const response = await getIntegrationEmployees(customerId, integrationType)
    const data = get(response, 'data', [])
    setHasIntegration(response?.status === 200)
    setIntegrationMap(keyBy(data, 'employeeId'))
  } catch (err) {
    setHasIntegration(false)
    console.error(`getIntegrationEmployees ${integrationType} error`, err)
  }
  setLoading(false)
}

const useIntegrationRequest = ({ customerId, integrationType }) => {
  const [loading, setLoading] = useState(false)
  const [hasIntegration, setHasIntegration] = useState(false)
  const [integrationMap, setIntegrationMap] = useState({})
  const [reload, setReload] = useState({})

  useEffect(() => {
    if (customerId) {
      fetchIntegrationEmployees({
        customerId,
        integrationType,
        setLoading,
        setIntegrationMap,
        setHasIntegration,
      })
    }
  }, [customerId, integrationType, reload])

  return {
    loading,
    hasIntegration,
    integrationMap,
    setReload,
  }
}

export const useIntegrationEmployeesForEmployees = ({ customerId }) => {
  const [selectedIntegration, setSelectedIntegration] = useState({})
  const [selectedIntegrationType, setSelectedIntegrationType] = useState(OFFICE365)

  const {
    loading: m365Loading,
    hasIntegration: m365HasIntegration,
    integrationMap: m365Integration,
    setReload: m365SetReload,
  } = useIntegrationRequest({ customerId, integrationType: OFFICE365 })

  const {
    loading: gwsLoading,
    hasIntegration: gwsHasIntegration,
    integrationMap: gwsIntegration,
    setReload: gwsSetReload,
  } = useIntegrationRequest({ customerId, integrationType: GWS })

  const integrationLoading = m365Loading || gwsLoading
  const hasIntegration = m365HasIntegration || gwsHasIntegration

  useEffect(() => {
    if (!integrationLoading) {
      if (m365HasIntegration) {
        setSelectedIntegration(m365Integration)
        setSelectedIntegrationType(OFFICE365)
      } else if (gwsHasIntegration) {
        setSelectedIntegration(gwsIntegration)
        setSelectedIntegrationType(GWS)
      } else {
        setSelectedIntegration({})
        setSelectedIntegrationType(OFFICE365)
      }
    }
  }, [gwsHasIntegration, gwsIntegration, integrationLoading, m365HasIntegration, m365Integration])

  const reloadIntegration = () => {
    gwsSetReload({})
    m365SetReload({})
  }

  const updateSelectedIntegration = useCallback(
    (integrationType) => {
      if (integrationType === OFFICE365) {
        setSelectedIntegration(m365Integration)
        setSelectedIntegrationType(OFFICE365)
      } else {
        setSelectedIntegration(gwsIntegration)
        setSelectedIntegrationType(GWS)
      }
    },
    [gwsIntegration, m365Integration],
  )

  return {
    integrationLoading,
    hasIntegration,
    m365HasIntegration,
    gwsHasIntegration,
    selectedIntegration,
    selectedIntegrationType,
    updateSelectedIntegration,
    reloadIntegration,
  }
}
